import React, { useState } from 'react';
import { BuyCreditsModal as SharedBuyCreditsModal, createCreditCheckoutViaSupabase } from '@rehub/buy-credits';
import { CREDIT_EXPIRY_COPY, IMAGEMOTION_DOUBLE_CREDIT_OFFERS, type CreditCurrency, type DoubleCreditOfferTier } from '@rehub/pricing';
import { supabase } from '../services/supabase';
import DoubleCreditOffer, { getDoubleCreditPriceId } from './DoubleCreditOffer';
import { creditBalanceRange, trackImagemotionEvent } from '../services/analytics';

interface BuyCreditsModalProps {
  userId: string;
  userCredits: number;
  onClose: () => void;
  doubleCreditEligible?: boolean;
  highlightOffer?: boolean;
}

const BuyCreditsModal: React.FC<BuyCreditsModalProps> = ({
  userId,
  userCredits,
  onClose,
  doubleCreditEligible = false,
  highlightOffer = false,
}) => {
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [offerError, setOfferError] = useState<string | null>(null);

  const handleDoubleCredit = async (tier: DoubleCreditOfferTier, currency: CreditCurrency) => {
    const offer = IMAGEMOTION_DOUBLE_CREDIT_OFFERS[tier];
    const priceId = getDoubleCreditPriceId(tier, currency);
    if (!priceId) {
      setOfferError(`Double-credit offer is not configured for ${currency}. Please contact support or buy a regular pack.`);
      return;
    }
    trackImagemotionEvent('double_credit_checkout_started', userId, {
      offer_tier: tier,
      currency,
      offer_eligible: true,
      credit_balance_range: creditBalanceRange(userCredits),
    });
    setProcessingId(`double_${tier}`);
    try {
      const url = await createCreditCheckoutViaSupabase(supabase, {
        currency,
        mode: 'payment',
        credits: offer.totalCredits,
        priceId,
        userId,
        purchaseType: 'custom_credits',
        successUrl: `https://hub.rehub.studio/#/purchase-complete?app=imagemotion&type=credits&credits=${offer.totalCredits}&return=${encodeURIComponent(window.location.origin + (window.location.pathname || '/'))}&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/?canceled=true`,
        extraMetadata: {
          offer_type: tier,
          purchased_credits: String(offer.purchasedCredits),
          bonus_credits: String(offer.bonusCredits),
          product_name: 'imagemotion_double_credit',
          purchase_type: 'double_credit_offer',
        },
      });
      window.location.href = url;
    } catch (err: unknown) {
      setOfferError(err instanceof Error ? err.message : 'Checkout failed.');
      setProcessingId(null);
    }
  };

  return (
    <SharedBuyCreditsModal
      userId={userId}
      credits={userCredits}
      onClose={onClose}
      appSource="imagemotion"
      accent="purple"
      welcomeOfferEligible={doubleCreditEligible}
      createCheckout={(params) => createCreditCheckoutViaSupabase(supabase, params)}
      onAnalytics={(event, props) => {
        if (event === 'buy_credits_panel_opened') {
          trackImagemotionEvent('buy_credits_clicked', userId, {
            credit_balance_range: creditBalanceRange(userCredits),
            offer_eligible: doubleCreditEligible,
            currency: props?.currency,
            source: 'buy_credits_panel',
          });
        }
        trackImagemotionEvent(event as any, userId, {
          ...props,
          credit_balance_range: creditBalanceRange(userCredits),
        });
      }}
      offerSlot={
        doubleCreditEligible
          ? ({ currency }) => (
              <div className="space-y-2">
                {offerError && (
                  <p className="text-sm text-red-300" role="alert">{offerError}</p>
                )}
                <DoubleCreditOffer
                  userId={userId}
                  currency={currency}
                  processingId={processingId}
                  onSelect={(tier) => handleDoubleCredit(tier, currency)}
                  variant={highlightOffer ? 'completion' : 'panel'}
                />
                <p className="text-[11px] text-slate-500">
                  Welcome offer is separate from regular packs. {CREDIT_EXPIRY_COPY.short}
                </p>
              </div>
            )
          : undefined
      }
    />
  );
};

export default BuyCreditsModal;
